import { useEffect, useState } from "react";
import axios from "axios";
import IEmailCampaign from "../types/IEmailCampaign";

interface Recipient {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  city: string;
  state: string;
  zip_code: string;
  filter_reason: string; // "Matched Zip Code", "Matched Tag"
  filter_detail: string | null; // The actual matched value (e.g., "90210" or "VIP")
}

interface SendCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: IEmailCampaign;
}

const SendCampaignModal: React.FC<SendCampaignModalProps> = ({
  isOpen,
  onClose,
  campaign,
}) => {
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewingRecipients, setViewingRecipients] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchRecipients();
    }
  }, [isOpen]);

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/outreach/email-campaign/${campaign.id}/recipients`
      );

      setRecipientCount(response.data.length);
      setRecipients(response.data || []);
    } catch (err) {
      setError("Failed to fetch recipient data.");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/outreach/email-campaign/${campaign.id}/send`
      );
      alert("Campaign sent successfully!");
      onClose();
    } catch (err) {
      setError("Failed to send campaign.");
    } finally {
      setLoading(false);
    }
  };

  return isOpen ? (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-[700px] max-h-[80vh] overflow-hidden">
        {viewingRecipients ? (
          // Recipient List View
          <>
            <h2 className="text-lg font-semibold mb-4">Recipient List</h2>
            <div className="max-h-60 overflow-y-auto border p-2 rounded-md">
              {recipients.length > 0 ? (
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b">
                      <th className="p-2">Recipient</th>
                      <th className="p-2">Email</th>
                      <th className="p-2">Filter Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((recipient, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-2">
                          {recipient.company_name || `${recipient.first_name} ${recipient.last_name}`}
                        </td>
                        <td className="p-2">{recipient.email}</td>
                        <td className="p-2 text-sm text-gray-600">
                          {recipient.filter_reason}{" "}
                          {recipient.filter_detail ? `(${recipient.filter_detail})` : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-600">No recipients found.</p>
              )}
            </div>
            <button
              className="mt-4 px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500"
              onClick={() => setViewingRecipients(false)}
            >
              Back
            </button>
          </>
        ) : (
          // Send Campaign View
          <>
            <h2 className="text-lg font-semibold mb-4">Send Email Campaign</h2>
            <p className="text-gray-700">
              Are you sure you want to send "<strong>{campaign.name}</strong>"?
            </p>
            {loading ? (
              <p className="text-gray-600 mt-2">Fetching recipient data...</p>
            ) : error ? (
              <p className="text-red-500">{error}</p>
            ) : (
              <>
                <p className="text-gray-700 mt-2">
                  This email will be sent to <strong>{recipientCount}</strong> recipients.
                </p>
                {recipientCount && recipientCount > 0 ? (
                  <p
                    className="text-blue-500 cursor-pointer hover:underline mt-2"
                    onClick={() => setViewingRecipients(true)}
                  >
                    View recipients
                  </p>
                ) : null}
              </>
            )}
            <div className="flex justify-end gap-4 mt-4">
              <button
                className="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                onClick={handleSend}
                disabled={loading || recipientCount === 0}
              >
                {loading ? "Loading..." : "Send"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;
};

export default SendCampaignModal;
